import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Hero from "../components/Hero";
import EventsCarousel from "../components/EventsCarousel";
import { ROSEBAR } from "../lib/constants";
import { sortEventsByUpcomingDay } from "../lib/eventSort";
import useMeta from "../lib/useMeta";
import { ArrowUpRight, Crown, Sparkles, Music, ShieldCheck, Star, Hand, ChevronRight } from "lucide-react";

const SectionLabel = ({ kicker, title, lead, align = "center", accent = "outcomes." }) => {
  // splits title at the placeholder marker {accent}
  const parts = title.split("{accent}");
  return (
    <div className={`max-w-4xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <span className="label-eyebrow text-[#B76E79]">{kicker}</span>
      <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] text-white tracking-tighter mt-5">
        {parts[0]}
        <span className="font-display-italic text-gradient-rose whitespace-nowrap">{accent}</span>
        {parts[1]}
      </h2>
      {lead && <p className={`mt-5 text-base md:text-lg text-white/60 leading-relaxed ${align === "center" ? "max-w-2xl mx-auto" : "max-w-2xl"}`}>{lead}</p>}
    </div>
  );
};

const WhyCard = ({ icon, title, desc, testid }) => (
  <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7 hover:border-[#B76E79]/40 transition-all duration-500" data-testid={testid}>
    <div className="w-11 h-11 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center mb-5">
      {icon}
    </div>
    <h3 className="text-white font-semibold text-base md:text-lg">{title}</h3>
    <p className="text-sm text-white/55 mt-2 leading-relaxed">{desc}</p>
  </div>
);

const StatBox = ({ label, value, sub, big = false }) => (
  <div className={`rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-5 md:p-7 ${big ? "md:p-8" : ""}`}>
    <div className="label-eyebrow text-white/55">{label}</div>
    <div className="font-display text-4xl md:text-5xl lg:text-6xl text-gradient-rose mt-2 leading-none">{value}</div>
    {sub && <div className="text-xs text-white/45 mt-2">{sub}</div>}
  </div>
);

export default function Home() {
  useMeta({
    title: "ROSEBAR LOUNGE — A premier night club in Washington DC",
    description: "Discover the line-up at ROSEBAR LOUNGE DC — the premier night club in Washington DC. Reserve your table for events and special occasions. Check out our gallery for guest appearances by Future, Migos, Jeezy, and more. Book now!",
    path: "/",
  });
  const { data: rawEvents = [] } = useQuery({
    queryKey: ["events"],
    queryFn: () => api.get("/events").then((r) => r.data),
  });
  const events = sortEventsByUpcomingDay(rawEvents);
  const { data: celebs = [] } = useQuery({
    queryKey: ["celebrities"],
    queryFn: () => api.get("/celebrities").then((r) => r.data),
  });

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Upcoming Events — directly under hero */}
      <section className="relative py-20 md:py-24 max-w-[1400px] mx-auto px-6 md:px-12" data-testid="upcoming-events">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
          <div className="max-w-2xl">
            <span className="label-eyebrow text-white/45">— What's On</span>
            <h2 className="font-display text-4xl md:text-6xl text-white mt-3 leading-[1.05] tracking-tight">
              Upcoming <span className="font-display-italic text-gradient-rose">events.</span>
            </h2>
          </div>
          <Link to="/events" className="btn-secondary self-start md:self-end shrink-0" data-testid="all-events-link">
            All events <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* Swipe / arrow cue — visible on mobile + desktop */}
        {events.length > 1 && (
          <div
            className="mb-6 inline-flex items-center gap-3 rounded-full border border-[#B76E79]/40 bg-[#B76E79]/12 backdrop-blur px-4 py-2.5"
            data-testid="upcoming-swipe-cue"
          >
            <ChevronRight size={16} className="text-[#E8B4B8] rotate-180 animate-swipe-hint" style={{ animationDirection: "reverse" }} strokeWidth={2.5} />
            <Hand size={15} className="text-[#E8B4B8] animate-swipe-hint md:hidden" strokeWidth={1.75} />
            <span className="text-[10.5px] tracking-[0.22em] uppercase text-white/90 font-semibold whitespace-nowrap">
              <span className="md:hidden">Swipe · {events.length} weekly nights · book any future date</span>
              <span className="hidden md:inline">Slide for more · {events.length} weekly nights · book any future date</span>
            </span>
            <ChevronRight size={16} className="text-[#E8B4B8] animate-swipe-hint" strokeWidth={2.5} />
          </div>
        )}

        <EventsCarousel events={events.slice(0, 12)} />
      </section>

      {/* Why Rosebar — feature card grid */}
      <section className="relative py-24 md:py-32 max-w-[1300px] mx-auto px-6 md:px-12" data-testid="why-rosebar">
        <SectionLabel
          kicker="Why Rosebar"
          title="A nightlife experience designed around {accent}"
          accent="the moment."
          lead="We don't measure nights in covers sold. We measure them in tables that came back. Sparklers that lit up Instagram. Celebrity moments that put DC on the map."
        />

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <WhyCard
            testid="why-vip"
            icon={<Crown size={20} className="text-[#E8B4B8]" />}
            title="VIP, the Right Way"
            desc="Deck, cabin, and birthday packages from $500. $300 deposit credited to your spend. Sparklers, every bottle."
          />
          <WhyCard
            testid="why-celebrity"
            icon={<Star size={20} className="text-[#E8B4B8]" />}
            title="The Celebrity Stop"
            desc="From Future to Angel Reese — Rosebar is the DC stop on every tour. The room everyone shows up to."
          />
          <WhyCard
            testid="why-music"
            icon={<Music size={20} className="text-[#E8B4B8]" />}
            title="The Sound of DC"
            desc="Afrobeats, Amapiano, Hip-Hop, R&B, Top 40, House. A different identity every night, every weekend."
          />
          <WhyCard
            testid="why-service"
            icon={<ShieldCheck size={20} className="text-[#E8B4B8]" />}
            title="Concierge On-Call"
            desc="Text (202) 301-8794 — 24/7. Birthdays, buyouts, last-minute tables. We handle the night."
          />
        </div>
      </section>

      {/* Stats Row */}
      <section className="relative py-12 md:py-16" data-testid="stats-row">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatBox label="Celebrities Hosted" value="200+" sub="Artists · Athletes · Influencers" />
          <StatBox label="Tables Per Weekend" value="120+" sub="Deck · Cabin · Birthday" />
          <StatBox label="Years in Dupont" value="10+" sub="DC's nightlife anchor" />
          <StatBox label="Sold-Out Saturdays" value="98%" sub="Reserve early" />
        </div>
      </section>

      {/* Cinematic hero image overlay with stats */}
      <section className="relative py-20 md:py-28" data-testid="hero-overlay-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img
              src="https://customer-assets.emergentagent.com/job_rosebar-vip/artifacts/uucpktuz_DN8A7657.jpg"
              alt="Rosebar interior"
              loading="lazy"
              decoding="async"
              className="w-full h-[420px] md:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/30 to-black/70" />

            {/* Floating stat tiles */}
            <div className="absolute inset-0 p-6 md:p-12 grid md:grid-cols-3 gap-4 md:gap-6 content-between">
              <div className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md p-5 md:p-6 self-start">
                <div className="label-eyebrow text-white/55">VIP Tables Booked</div>
                <div className="font-display text-3xl md:text-5xl text-white mt-2 leading-none">8,420</div>
                <div className="text-xs text-[#B76E79] mt-2 tracking-wider">↗ +12% YoY</div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md p-5 md:p-6 self-center md:justify-self-center">
                <div className="label-eyebrow text-white/55">Bottles Popped</div>
                <div className="font-display text-3xl md:text-5xl text-gradient-rose mt-2 leading-none">26,180</div>
                <div className="text-xs text-white/55 mt-2">Champagne · Premium · Top-Shelf</div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-md p-5 md:p-6 self-end md:justify-self-end">
                <div className="label-eyebrow text-white/55">Avg Saturday Capacity</div>
                <div className="font-display text-3xl md:text-5xl text-white mt-2 leading-none">94%</div>
                <div className="text-xs text-white/45 mt-2">Sold-out by 11:30PM</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Experiences section removed per request */}

      {/* Celebrity strip hidden until official photos are uploaded */}

      {/* Private + Membership split (kept simple, rounded) */}
      <section className="py-24 md:py-32 max-w-[1400px] mx-auto px-6 md:px-12" data-testid="private-membership-split">
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/private-events" className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#B76E79]/50 aspect-[16/11] transition-all duration-500" data-testid="home-private-link">
            <img src="https://customer-assets.emergentagent.com/job_rosebar-vip/artifacts/xedw93zl_1-ff3af4fe.jpeg" alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform" style={{ transitionDuration: "1200ms" }} />
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/40 to-transparent" />
            <div className="relative h-full p-8 md:p-12 flex flex-col justify-end">
              <span className="label-eyebrow text-white/60">Buyouts & Private Events</span>
              <h3 className="font-display text-4xl md:text-5xl text-white mt-3 leading-tight">Host the night that<br /><span className="font-display-italic text-gradient-rose">becomes the story.</span></h3>
              <p className="text-white/65 mt-4 max-w-md text-sm md:text-base">Corporate · Birthday · Album release · Brand activation. We handle the night.</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#E8B4B8]">Start an inquiry <ArrowUpRight size={14} /></span>
            </div>
          </Link>
          <Link to="/membership" className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#D4AF7A]/50 aspect-[16/11] transition-all duration-500" data-testid="home-membership-link">
            <img src="https://customer-assets.emergentagent.com/job_rosebar-vip/artifacts/u5g0bw76_day-17.jpeg" alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:scale-105 transition-transform" style={{ transitionDuration: "1200ms" }} />
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/50 to-transparent" />
            <div className="relative h-full p-8 md:p-12 flex flex-col justify-end">
              <span className="label-eyebrow text-[#D4AF7A]">Membership</span>
              <h3 className="font-display text-4xl md:text-5xl text-white mt-3 leading-tight">The <span className="font-display-italic text-gradient-gold">Inner Circle.</span></h3>
              <p className="text-white/65 mt-4 max-w-md text-sm md:text-base">Skip-the-line. Comp cover. Celebrity event access. Concierge.</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm text-[#D4AF7A]">View plans <ArrowUpRight size={14} /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* Visit (clean centered) */}
      <section className="relative py-24 md:py-32 max-w-[1100px] mx-auto px-6 md:px-12 text-center" data-testid="visit-section">
        <span className="label-eyebrow text-[#B76E79]">Visit Rosebar</span>
        <h2 className="font-display text-4xl md:text-6xl text-white mt-4 leading-[1.05] tracking-tighter">
          1215 Connecticut Ave NW
          <br />
          <span className="font-display-italic text-gradient-rose">Dupont Circle.</span>
        </h2>
        <p className="text-white/60 mt-5 max-w-xl mx-auto leading-relaxed">
          Steps from Dupont Metro. Valet on-site. 21+ with valid ID. Open Tuesday through Sunday.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={ROSEBAR.google_maps} target="_blank" rel="noreferrer" className="btn-primary" data-testid="visit-directions">Get Directions <ArrowUpRight size={14} /></a>
          <a href={`sms:${ROSEBAR.phone_text}`} className="btn-secondary" data-testid="visit-text">Text Concierge</a>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-left">
          {ROSEBAR.hours.map((h) => (
            <div key={h.day} className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4">
              <div className="label-eyebrow text-white/40">{h.day}</div>
              <div className="text-white mt-1 text-sm">{h.hours}</div>
              {h.note && <div className="text-[10px] text-[#B76E79] mt-1 tracking-wide">{h.note}</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
